import * as ng from 'angular';

export default (module: ng.IModule) => {
    module.component('tyEventPaymentModelForm', {
        bindings: {
            onChanges: '&',
            privacy: '<',
            defaultProductId: '<',
            viewMode: '@?',
            occurrence: '<',
        },
        template: require('./event-payment-model-form.component.html').default,
        controller: EventPaymentModelFormController,
    });
};

interface PaymentType {
    key: 'free' | 'one-time' | 'recurring';
    isRecurring: boolean;
    label: string;
    description: string;
    iconClass: string;
}

interface ProductData {
    productId: number;
    label: string;
}

interface PayToJoinPricingFormData {
    paymentType: PaymentType;
    product?: ProductData;
}

interface XYardsProduct {
    id: number;
    price: string;
    name: string;
}

const PAYMENT_TYPES: PaymentType[] = [
    {
        key: 'free',
        isRecurring: false,
        label: 'FREE',
        description: 'Everyone can join. Free of charge.',
        iconClass: 'fa-star',
    },
    {
        key: 'one-time',
        isRecurring: false,
        label: 'One-time Payment',
        description: 'A single payment will grant permanent access.',
        iconClass: 'fa-tag',
    },
    {
        key: 'recurring',
        isRecurring: true,
        label: 'Recurring Payment',
        description: 'User will be charged every month to stay participants.',
        iconClass: 'fa-tags',
    },
];

const mapProduct = (product: XYardsProduct): ProductData => {
    return { productId: product.id, label: product.name };
};

class EventPaymentModelFormController implements ng.IComponentController {
    static $inject = ['$http'];

    viewMode: 'create' | 'edit' = 'create';
    privacy?: 'public' | 'private' | 'protected';
    onChanges?: (data: { $value: PayToJoinPricingFormData }) => void;
    occurrence?: string | 'one-session';

    formData: PayToJoinPricingFormData = {
        paymentType: PAYMENT_TYPES[0],
    };

    selectableProducts: ProductData[] = [];

    // something is loading
    isLoading: boolean = false;

    constructor(private $http: ng.IHttpService) {}

    triggerChange() {
        this.onChanges && this.onChanges({ $value: this.formData });
    }

    $onInit() {
        this._initFormData();
    }

    $onChanges(onChangesObj: ng.IOnChangesObject) {
        this._initFormData();
        this.triggerChange();
    }

    get isCreateView() {
        return this.viewMode == 'create';
    }

    get requiresPaidPlan() {
        return this.privacy != 'public';
    }

    get isRecurring() {
        return !['one-session'].includes(this.occurrence ?? '');
    }

    get SELECTABLE_PAYMENT_TYPES() {
        const privacyLevel = this.privacy ?? 'public';

        let validTypes: string[];

        if ('public' === privacyLevel) {
            validTypes = ['free'];
        } else {
            validTypes = ['one-time'];
            if (this.isRecurring) validTypes.push('recurring');
        }

        return PAYMENT_TYPES.filter((pt) => validTypes.includes(pt.key));
    }

    onPaymentTypeChange() {
        // we need to set the product or it will be blank.
        this._fetchSelectableProducts(this.formData.paymentType.isRecurring).then((products) => {
            this.selectableProducts = products;
            this.formData.product = this.selectableProducts[0];
            this.triggerChange();
        });
    }

    onProductChange() {
        this.triggerChange();
    }

    _initFormData() {
        const paymentType = this.SELECTABLE_PAYMENT_TYPES[0];
        this.formData.paymentType = paymentType;

        this._fetchSelectableProducts(paymentType.isRecurring).then((products) => {
            this.selectableProducts = products;
            this.formData.product = this.selectableProducts[0];
        });
    }

    _fetchSelectableProducts(recurring: boolean): ng.IPromise<ProductData[]> {
        const params = { entry: 'webinar', recurring: recurring ? 1 : 0 };

        this.isLoading = true;

        return this.$http
            .get<{ data: XYardsProduct[] }>('/api/v1.0/xyards/products', { params })
            .then((response) => response.data.data)
            .then((products) => {
                return products.map(mapProduct);
            })
            .finally(() => (this.isLoading = false));
    }
}
